import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Struggler from "../../../assets/The Silent Struggler.png";
import Outsider from "../../../assets/The Charming Outsider.png";
import Natural from "../../../assets/The Corporate Natural.png";
import Splitter from "../../../assets/The Identity Splitter.png";
import Reformer from "../../../assets/The Reformer in Waiting.png";
import Explorer from "../../../assets/The Reluctant Explorer.png";
import star from "../../../assets/stars.png";
import certificateVector from "../../../assets/CertificateVector.png";
import { LuShare2 } from "react-icons/lu";

// Mapping from persona letters to persona names
const personaMapping = {
  A: "Corporate Natural",
  B: "Silent Struggler",
  C: "Reformer in Waiting",
  D: "Charming Outsider",
  E: "Identity Splitter",
  F: "Reluctant Explorer",
};

const allPersonaData = {
  "Corporate Natural": {
    personaType: "Corporate Natural",
    subtitle: "Your Corporate alignment persona is",
    image: Natural,
    sections: [
      {
        title: "Core Trait",
        items: [
          "Thrives in structured systems and plays by the rules.",
          "Finds it easy to read the room and adjust quickly.",
          "Naturally gains trust through calm competence.",
        ],
      },
      {
        title: "Emotional Truth",
        items: [
          "Feels safest when being seen as dependable and 'put together'.",
          "Sometimes worries that slowing down will make them invisible.",
          "Keeps moving to stay relevant.",
        ],
      },
      {
        title: "What They're Good At",
        icon: "certificate",
        items: [
          "Owning deliverables without drama.",
          "Making things look easy, even when they aren't.",
          "Creating structure others can lean on.",
        ],
      },
      {
        title: "Your Ideal Manager",
        icon: "",
        items: [
          "Encourage them to lift others as they climb.",
          "Invite them to question what 'success' really means.",
          "Help them pause and reconnect to their 'why'.",
        ],
      },
      {
        title: "Hidden Cost",
        icon: "🔴",
        items: [
          "Can lose touch with their true opinions.",
          "Feels pressure to always 'perform' at 100%.",
          "Success may come at the cost of self-trust.",
        ],
      },
    ],
  },
  "Silent Struggler": {
    personaType: "Silent Struggler",
    subtitle: "Your Corporate alignment persona is",
    image: Struggler,
    sections: [
      {
        title: "Core Trait",
        items: [
          "Tends to withdraw rather than resist out loud.",
          "Doesn't rock the boat, even when it's sinking.",
          "Hides in plain sight.",
        ],
      },
      {
        title: "Emotional Truth",
        items: [
          "Often feels unseen or like a passenger.",
          "Struggles to name what they really want.",
          "Carries quiet hope that someone might notice.",
        ],
      },
      {
        title: "What They're Good At",
        icon: "certificate",
        items: [
          "Keeping things moving behind the scenes.",
          "Absorbing chaos without adding to it.",
          "Surviving in silence.",
        ],
      },
      {
        title: "Your Ideal Manager",
        icon: "",
        items: [
          "Offer space, not pressure, to share.",
          "Notice small efforts — they often feel huge.",
          "Encourage small acts of ownership.",
        ],
      },
      {
        title: "Hidden Cost",
        icon: "🔴",
        items: [
          "May burn out while pretending to be fine.",
          "Ideas stay buried for fear of judgment.",
          "Loneliness grows behind the silence.",
        ],
      },
    ],
  },
  "Reformer in Waiting": {
    personaType: "Reformer in Waiting",
    subtitle: "Your Corporate alignment persona is",
    image: Reformer,
    sections: [
      {
        title: "Core Trait",
        items: [
          "Has a clear sense of right and wrong.",
          "Struggles to stay silent when values are compromised.",
          "Feels pulled between change and self-preservation.",
        ],
      },
      {
        title: "Emotional Truth",
        items: [
          "Wants to fix things — but not at the cost of being labeled 'difficult.'",
          "Their silence isn't approval — it's fatigue.",
          "Bravery comes with an inner toll.",
        ],
      },
      {
        title: "What They're Good At",
        icon: "certificate",
        items: [
          "Spotting misalignment others ignore.",
          "Standing up for fairness.",
          "Keeping purpose alive in bureaucracy.",
        ],
      },
      {
        title: "Your Ideal Manager",
        icon: "",
        items: [
          "Don't just listen — amplify their voice.",
          "Give them allies, not just applause.",
          "Build safe ways for them to challenge the norm.",
        ],
      },
      {
        title: "Hidden Cost",
        icon: "🔴",
        items: [
          "Might carry the team's conscience alone.",
          "Can be seen as too intense or idealistic.",
          "Change can feel like a lonely burden.",
        ],
      },
    ],
  },
  "Charming Outsider": {
    personaType: "Charming Outsider",
    subtitle: "Your Corporate alignment persona is",
    image: Outsider,
    sections: [
      {
        title: "Core Trait",
        items: [
          "Observes from the edge with sharp clarity.",
          "Knows the game — chooses when to play.",
          "Uses wit to stay in control.",
        ],
      },
      {
        title: "Emotional Truth",
        items: [
          "Doesn't want to lose their uniqueness.",
          "Feels connection might cost independence.",
          "Keeps one foot out, just in case.",
        ],
      },
      {
        title: "What They're Good At",
        icon: "certificate",
        items: [
          "Spotting patterns others miss.",
          "Influencing sideways without authority.",
          "Saying what others are thinking — cleverly.",
        ],
      },
      {
        title: "Your Ideal Manager",
        icon: "",
        items: [
          "Give them room to roam and reflect.",
          "Invite them into trust, not expectation.",
          "Value their insight, not just their output.",
        ],
      },
      {
        title: "Hidden Cost",
        icon: "🔴",
        items: [
          "May never feel fully 'in.'",
          "Hard to build momentum alone.",
          "Connection feels risky — so they stay clever instead.",
        ],
      },
    ],
  },
  "Identity Splitter": {
    personaType: "Identity Splitter",
    subtitle: "Your Corporate alignment persona is",
    image: Splitter,
    sections: [
      {
        title: "Core Trait",
        items: [
          "Adapts constantly to please, belong, or avoid conflict.",
          "Knows how to read every room — but forgets their own voice.",
          "The glue in the group, invisible until it cracks.",
        ],
      },
      {
        title: "Emotional Truth",
        items: [
          "Unsure who they are when they're not adjusting.",
          "Feels valued for what they do, not who they are.",
          "Wants belonging without disappearing.",
        ],
      },
      {
        title: "What They're Good At",
        icon: "certificate",
        items: [
          "Bridging gaps between people and teams.",
          "Soothing tension before it escalates.",
          "Filling roles no one knew were empty.",
        ],
      },
      {
        title: "Your Ideal Manager",
        icon: "",
        items: [
          "Acknowledge their emotional labor.",
          "Help them find stable ground inside themselves.",
          "Encourage self-expression without consequence.",
        ],
      },
      {
        title: "Hidden Cost",
        icon: "🔴",
        items: [
          "Chronic exhaustion from constant shape-shifting.",
          "Praise never quite reaches the core.",
          "Visibility feels unsafe — but absence feels worse.",
        ],
      },
    ],
  },
  "Reluctant Explorer": {
    personaType: "Reluctant Explorer",
    subtitle: "Your Corporate alignment persona is",
    image: Explorer,
    sections: [
      {
        title: "Core Trait",
        items: [
          "Always wondering if this is the right path.",
          "Resists labels — even when craving clarity.",
          "Wanders more than climbs.",
        ],
      },
      {
        title: "Emotional Truth",
        items: [
          "Fears commitment will trap them.",
          "Doesn't want to fake it — even if everyone else does.",
          "Believes there's something more, somewhere.",
        ],
      },
      {
        title: "What They're Good At",
        icon: "certificate",
        items: [
          "Asking the questions others avoid.",
          "Noticing when cultures clash.",
          "Spotting discomfort others bury.",
        ],
      },
      {
        title: "Your Ideal Manager",
        icon: "",
        items: [
          "Let them prototype, not pledge.",
          "Offer flexibility and side quests.",
          "Help them explore purpose, not just roles.",
        ],
      },
      {
        title: "Hidden Cost",
        icon: "🔴",
        items: [
          "May float too long without landing.",
          "Unseen talents stay dormant.",
          "Isolation grows when they stop explaining.",
        ],
      },
    ],
  },
};

const DotRed = () => (
  <div className="w-8 h-8 bg-[#FFE5E5] rounded-full flex items-center justify-center">
    <div className="w-3 h-3 bg-[#FF0000B2] rounded-full"></div>
  </div>
);

const SectionCard = ({ title, items, icon }) => {
  const getIconColor = () => {
    if (icon === "🎯" || icon === "🧠") return "text-teal-500";
    return "";
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold font-gotham text-[#323232] text-lg sm:text-xl">
          {title}
        </h3>
        {icon &&
          (icon === "🔴" ? (
            <DotRed />
          ) : icon === "certificate" ? (
            <div className="w-8 h-8">
              <img
                src={certificateVector}
                alt="Certificate Icon"
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-lg ${getIconColor()}`}
            >
              {icon}
            </div>
          ))}
      </div>
      <ul className="space-y-3">
        {items.map((item, index) => (
          <li
            key={index}
            className="flex items-start gap-2 text-sm sm:text-base font-gotham font-medium text-[#323232] leading-relaxed"
          >
            <span className="w-1.5 h-1.5 bg-[#484848] rounded-full mt-2 flex-shrink-0"></span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PersonaIllustrationText = ({ personaData }) => {
  const handleShare = () => {
    const text = `I'm a ${personaData.personaType}! 💼✨\nFind out your corporate persona at`;
    const shareUrl = `https://clarity.mystocai.com/quiz`;

    if (navigator.share) {
      navigator
        .share({
          title: "Discover your Corporate Persona",
          text,
          url: shareUrl,
        })
        .catch((error) => {
          console.error("Sharing failed", error);
        });
    } else {
      // fallback: open Twitter share
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}`;
      window.open(twitterUrl, "_blank");
    }
  };

  return (
    <div className="flex flex-col items-center text-center w-full bg-[#F5F5F5] rounded-2xl pt-4 px-4 relative">
      <p className="text-sm font-gotham text-[#666666] font-medium mb-1">
        {personaData.subtitle}
      </p>
      <div className="flex gap-2">
        <span>
          <img src={star} alt="" className="w-7 h-7 -rotate-180" />
        </span>
        <h1 className="text-lg sm:text-xl text-[#323232] font-gotham font-medium mb-4">
          {personaData.personaType}
        </h1>
        <img src={star} alt="" className="w-7 h-7" />
      </div>
      <img src={personaData.image} alt="Persona" className="max-w-[140px]" />
      <div
        onClick={handleShare}
        className="w-10 h-10 rounded-full bg-[#54B0AF] flex justify-center items-center absolute right-5 bottom-5 cursor-pointer hover:bg-[#3f8e8c]"
      >
        <LuShare2 className="text-white text-xl font-bold" />
      </div>
    </div>
  );
};

const PersonaResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

  const [resultPersona, setResultPersona] = useState(
    location.state?.result_persona || null
  );
  const [loading, setLoading] = useState(!resultPersona);

  useEffect(() => {
    const fetchPersona = async () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (!storedUser) throw new Error("No user found in localStorage");

        const { user_id } = JSON.parse(storedUser);
        if (!user_id) throw new Error("User ID missing");

        const res = await fetch(`${apiBaseURL}/quiz/result/${user_id}`);
        if (!res.ok) throw new Error(`API error: ${res.status}`);

        const data = await res.json();
        if (data?.result_persona) {
          setResultPersona(data.result_persona);
        } else {
          throw new Error("No persona in result");
        }
      } catch (error) {
        console.error("Failed to fetch result persona:", error.message);
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    if (!resultPersona) {
      fetchPersona();
    } else {
      setLoading(false);
    }

    localStorage.removeItem("temp_user_id");
  }, [resultPersona, apiBaseURL, navigate]);

  const personaName = personaMapping[resultPersona] || "Reluctant Explorer";
  const personaData = allPersonaData[personaName];

  const handleRetakeQuiz = () => {
    navigate("/quiz?retake=true");
  };

  if (loading || !personaData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-gray-600 font-gotham">
          {loading ? "Loading your result..." : "No persona result available."}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Top Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm p-4 h-full flex flex-col md:flex-row gap-6">
              {/* Core Trait */}
              <div className="flex-1">
                <h3 className="font-bold text-[#323232] font-gotham text-lg sm:text-xl mb-4">
                  {personaData.sections[0].title}
                </h3>
                <ul className="space-y-3">
                  {personaData.sections[0].items.map((item, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-2 text-sm sm:text-base font-gotham font-medium text-[#484848] leading-relaxed"
                    >
                      <span className="w-1.5 h-1.5 bg-[#484848] rounded-full mt-2 flex-shrink-0"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Illustration */}
              <div className="flex-1 flex items-center justify-center">
                <PersonaIllustrationText personaData={personaData} />
              </div>
            </div>
          </div>
          {/* Emotional Truth */}
          <div className="lg:col-span-1">
            <SectionCard
              title={personaData.sections[1].title}
              items={personaData.sections[1].items}
            />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {personaData.sections.slice(2).map((section, idx) => (
            <SectionCard
              key={idx}
              title={section.title}
              items={section.items}
              icon={section.icon}
            />
          ))}
        </div>

        <div className="text-center mt-12 flex flex-col md:flex-row gap-4 justify-center items-center">
          <button
            onClick={handleRetakeQuiz}
            className="px-6 py-3 bg-[#54B0AF] hover:bg-teal-600 text-white font-bold rounded-full text-sm sm:text-base transition-all"
          >
            Retake the Quiz
          </button>
          <button
            onClick={() =>
              (window.location.href = "https://clarity.mystocai.com")
            }
            className="px-6 py-3 bg-[#54B0AF] hover:bg-teal-600 text-white rounded-full font-bold text-sm sm:text-base transition-all"
          >
            Explore Stocai
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonaResult;
